<template>
  <div class="maint-overview">
    <div class="maint-header">
      <h2 class="maint-title">Manutenção</h2>
      <p class="maint-subtitle">Selecione uma obra para gerenciar rotinas e Ordens de Serviço.</p>
    </div>

    <MyServiceOrdersCard />

    <template v-if="accessLevel >= 2">
      <div v-if="loading" class="maint-loading">Carregando projetos...</div>

      <div v-else-if="projects.length === 0" class="maint-empty">
        Nenhum projeto cadastrado.
      </div>

      <div v-else class="maint-project-grid">
        <button
          v-for="proj in projects"
          :key="proj.id"
          class="maint-project-card"
          type="button"
          @click="selectProject(proj)"
        >
          <div class="maint-card-photo">
            <img v-if="hasPhoto(proj.photoDataUrl)" class="maint-photo-img" :src="$resolveUrl(proj.photoDataUrl)" :alt="proj.name" />
            <div v-else class="maint-photo-empty">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </div>
            <div class="maint-card-wrench">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            </div>
          </div>
          <div class="maint-card-body">
            <div class="maint-card-name">{{ proj.name }}</div>
            <div class="maint-card-counts">
              <span>{{ osCountFor(proj.id) }} OS</span>
            </div>
            <div
              class="maint-status-badge"
              :style="statusStyle(proj.id)"
            >
              {{ statusLabel(proj.id) }}
            </div>
          </div>
        </button>
      </div>
    </template>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { projectsStore, type Project } from '@/store/projects'
import { maintenanceStore } from '@/store/maintenance'
import { systemStore } from '@/store/system'
import MyServiceOrdersCard from '@/components/dashboard/MyServiceOrdersCard.vue'

export default defineComponent({
  name: 'MaintenanceOverviewPanel',
  components: { MyServiceOrdersCard },
  props: {
    isAdmin: { type: Boolean, default: false },
    accessLevel: { type: Number, default: 0 }
  },
  setup(props) {
    const router = useRouter()
    const loading = ref(true)

    const projects = computed(() => projectsStore.all())

    const hasPhoto = (v: string) => typeof v === 'string' && (v.startsWith('data:') || v.startsWith('/uploads/'))

    const ordersFor = (projectId: string) =>
      maintenanceStore.serviceOrders.items.filter(os => os.projectId === projectId && os.status !== 'cancelled')

    const osCountFor = (projectId: string) => ordersFor(projectId).length

    const projectStatus = (projectId: string): 'pronto' | 'pendente' | 'sem-os' => {
      const orders = ordersFor(projectId)
      if (!orders.length) return 'sem-os'
      return orders.every(os => os.status === 'approved') ? 'pronto' : 'pendente'
    }

    const statusLabel = (projectId: string): string => {
      const status = projectStatus(projectId)
      if (status === 'sem-os') return 'Sem OS designada'
      const cfg = systemStore.get()
      return status === 'pronto'
        ? (cfg.maintenanceProntoText || 'Pronto')
        : (cfg.maintenancePendenteText || 'Pendente procedimento')
    }

    const statusStyle = (projectId: string) => {
      const status = projectStatus(projectId)
      const cfg = systemStore.get()
      const color = status === 'pronto'
        ? (cfg.maintenanceProntoColor || '#16a34a')
        : status === 'sem-os'
          ? '#9333ea'
          : (cfg.maintenancePendenteColor || '#dc2626')
      return {
        background: color + '1a',
        color,
        borderColor: color + '55'
      }
    }

    const selectProject = (proj: Project) => {
      router.push('/manutencao/' + encodeURIComponent(proj.id))
    }

    onMounted(async () => {
      try {
        if (projectsStore.all().length === 0) await projectsStore.refresh()
        if (props.accessLevel >= 2) await maintenanceStore.loadServiceOrders()
      } finally {
        loading.value = false
      }
    })

    return {
      loading,
      projects,
      hasPhoto,
      osCountFor,
      statusLabel,
      statusStyle,
      selectProject
    }
  }
})
</script>

<style scoped lang="scss">
.maint-overview {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding: 24px 28px;
  overflow-y: auto;
  height: 100%;
  box-sizing: border-box;
}

.maint-header { display: flex; flex-direction: column; gap: 4px; }
.maint-title { font-size: 20px; font-weight: 700; color: var(--ink); margin: 0; }
.maint-subtitle { font-size: 13px; color: var(--ink-muted); margin: 0; }

.maint-loading, .maint-empty {
  font-size: 13px; color: var(--ink-muted); padding: 32px 0; text-align: center;
}

.maint-project-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  align-items: start;
}

.maint-project-card {
  display: flex;
  flex-direction: column;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  padding: 0;
  transition: box-shadow .15s ease, border-color .15s ease, transform .12s ease;

  &:hover {
    border-color: var(--accent);
    box-shadow: 0 4px 16px rgba(0,0,0,.10);
    transform: translateY(-2px);
  }
}

.maint-card-photo {
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 */
  background: rgba(31,30,26,.05);
  flex-shrink: 0;
  overflow: hidden;
}

.maint-photo-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.maint-photo-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(31,30,26,.25);
}

.maint-card-wrench {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(251,191,36,.85);
  color: rgba(120,80,0,.95);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.maint-card-body {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.maint-card-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.maint-card-counts {
  font-size: 11px;
  color: var(--ink-muted);
  display: flex;
  gap: 5px;
}

.maint-sep { opacity: .5; }

.maint-status-badge {
  display: inline-block;
  align-self: flex-start;
  margin-top: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.6;
}
</style>
